import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useViewport } from 'utils'

// next components
import Head from 'next/head'
import Image from 'next/image'

// solana
import { Connection, PublicKey } from '@solana/web3.js'
import { Metaplex } from '@metaplex-foundation/js'
import { useWallet } from '@solana/wallet-adapter-react'

// components
import MainSection from 'components/pages/BrowseNFT/MainSection'
import MerchSection from 'components/pages/BrowseNFT/MerchSection'

// custom functions
import { getNftFromDAS, getNftFromRpc, getNFTOwner } from 'utils/nfts.utils'

// images and icons
import CloseIcon from 'assets/icons/close-screen.svg'
import ArrowRightIcon from 'assets/icons/arrow-right-line.svg?component'

// third party components
import { useDispatch, useSelector } from 'react-redux'

// styles
import styles from './index.module.scss'

// resources
import DefaultNftBG from 'assets/images/default-nft-bg.png'
import ShopApparelList from 'assets/json/browse-nft/shop-apparel-list.json'

import nftsAPI from 'apis/nfts'

import { isString, optimizeCdnUrlOgImage } from 'utils/helpers'
import { getAbsoluteOgUrl } from 'utils/seo'
import ModelViewer from 'components/ModelViewer'
import { NftData } from 'types/nft'
import SecondaryLayout from 'components/Layout/SecondaryLayout'

export interface EditionDetail {
  supply: number
  maxSupply?: number
  print?: {
    number: number
  }
  isOriginal: boolean
  isSingleNft: boolean
  isOpenEdition: boolean
  isLimitedEdition: boolean
}

export interface BrowseNFTProps {
  nftDetail?: NftData
  nftOwner?: any
  editionDetail?: EditionDetail
  error?: string
}

const BrowseNFTContent = ({ nftDetail, nftOwner, editionDetail }: BrowseNFTProps) => {
  const router = useRouter()
  const nftType = (nftDetail?.category as string) ?? 'image'
  const apparelType = useSelector<any>(state => state.apparelType)
  const detailShow = useSelector<any>(state => state.detailShow)
  const [openMerchPage, setOpenMerchPage] = useState(false)
  const [merchData, setMerchData] = useState(null)
  const [nftInfo, setNftInfo] = useState<NftData>()
  const { connected, autoConnect } = useWallet()
  const dispatch = useDispatch()
  const [merchImages, setMerchImages] = useState([])
  const [selectedMerchImage, setSelectedMerchImage] = useState('')

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }, 500)
    dispatch({ type: 'set', isLoading: false })
    dispatch({ type: 'set', printImageUrl: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setProcessMerchImage = (images, idx) => {
    let list = []
    images.map((image, index) => list.push({ url: image.mockup_url, selected: index === idx }))
    setMerchImages(list)
    setSelectedMerchImage(list[idx].url)
  }
  useEffect(() => {
    if (detailShow === true) {
      if (apparelType !== undefined || apparelType !== '' || apparelType !== null) {
        const nftName = nftDetail.name ?? 'No name found'
        const nftCoverUrl = nftDetail.original?.image ?? ''
        const nftUsername = ''
        const mintAddress = nftDetail.mintAddress.toString()
        const item = ShopApparelList.find(apparel => apparel.name === apparelType)
        let params = {
          nftName,
          nftUsername,
          nftCoverUrl,
          mintAddress,
          category: item?.category,
          subCategory: item?.sub_type,
          base: item?.base,
        }
        setMerchData(params)
        setOpenMerchPage(true)
        dispatch({ type: 'set', detailShow: false })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailShow, apparelType])

  useEffect(() => {
    const fetchData = async () => {
      if (!nftDetail) return
      if (connected) {
        await nftsAPI.getDetail(nftDetail.mintAddress).then(
          (response: any) => {
            if (!isString(response)) {
              setNftInfo(response)
              if (response.creator_address !== null) {
                dispatch({ type: 'set', merchBy: response.creator_username })
              }
            }
          },
          error => {}
        )
      } else {
        if (autoConnect) return
        await nftsAPI.get(nftDetail.mintAddress).then(
          (response: any) => {
            if (!isString(response)) {
              setNftInfo(response)
              if (response.creator_address !== null) {
                dispatch({ type: 'set', merchBy: response.creator_username })
              }
            }
          },
          error => {}
        )
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, nftDetail])

  let nftName = nftDetail?.name ?? ''
  if (editionDetail && !editionDetail?.isSingleNft) {
    let { print, supply, maxSupply } = editionDetail
    let supplyInfo = `#${print?.number}/${supply}`
    if (editionDetail.isOriginal) supplyInfo = `max ${maxSupply ?? '\u221e'}`
    else if (maxSupply != supply) supplyInfo = `edition ${print?.number}/${supply}, max ${maxSupply ?? '\u221e'}`
    nftName = `${nftName} (${supplyInfo})`
  }
  let nftTitle = `${nftName} | Foster Marketplace`
  let nftImageUrl = optimizeCdnUrlOgImage(getAbsoluteOgUrl(nftDetail?.image || DefaultNftBG.src))

  let nftDescription = nftDetail?.description ?? ''

  const [nftCoverUrl, setNftCoverUrl] = useState('')
  const [fullScreen, setFullScreen] = useState(false)
  const [fullScreenMerch, setFullScreenMerch] = useState(false)

  const setFullScreenView = url => {
    window.scrollTo({
      top: 0,
    })
    document.body.style.overflow = 'hidden'
    setFullScreen(true)
    setNftCoverUrl(url)
  }

  const reloadNftData = async () => {
    if (!nftDetail) return
    if (connected) {
      await nftsAPI.getDetail(nftDetail.mintAddress).then(
        (response: any) => {
          if (!isString(response)) {
            setNftInfo(response)
          }
        },
        error => {}
      )
    } else {
      await nftsAPI.get(nftDetail.mintAddress).then(
        (response: any) => {
          if (!isString(response)) {
            setNftInfo(response)
          }
        },
        error => {}
      )
    }
  }

  return (
    <div className="w-full min-h-full">
      <Head>
        <title>{nftTitle}</title>
        <meta property="description" content={nftDescription} />

        <meta name="og:title" content={nftTitle} />
        <meta property="og:description" content={nftDescription} />
        {nftType && <meta name="og:type" content={nftType} />}
        <meta name="og:image" content={nftImageUrl} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={nftTitle} />
        <meta property="twitter:description" content={nftDescription} />
        <meta property="twitter:image" content={nftImageUrl} />

        <link rel="icon" href="/logo.png" />
      </Head>
      <div className={styles.container}>
        {openMerchPage ? (
          <div className="w-full h-full flex justify-center z-10">
            <MerchSection
              data={merchData}
              openNftPage={() => {
                setOpenMerchPage(false)
              }}
            />
          </div>
        ) : (
          <div className="z-10 w-full h-full">
            {nftDetail &&
              (nftOwner === null ? (
                <div className="w-full flex justify-center lg:mt-[200px] mt-[150px]">
                  <div>
                    <div className={styles.noResult}>No results found</div>
                    <div className={styles.tokenFetch}>Token metadata unable to be fetched</div>
                    <div className="flex justify-center mt-[30px]">
                      <div className={styles.backHome} onClick={() => router.push('/')}>
                        Back Home
                        <ArrowRightIcon className={styles.rightIcon} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.mainBG}>
                  <div className="w-full h-full">
                    <MainSection
                      key={nftDetail.mintAddress.toString()}
                      nftDetail={nftDetail}
                      editionDetail={editionDetail}
                      nftInfo={nftInfo}
                      setFullScreenView={setFullScreenView}
                      reloadNftData={reloadNftData}
                      setOpenMerchPage={setOpenMerchPage}
                      setMerchData={setMerchData}
                      fullScreen={fullScreen}
                      setMerchImages={images => setProcessMerchImage(images, 0)}
                      setFullScreenMerch={setFullScreenMerch}
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {fullScreen && (
        <div className={styles.fullScreen}>
          {nftType?.includes('vr') ? (
            <ModelViewer path={nftCoverUrl} fullScreen />
          ) : (
            <img
              src={nftCoverUrl}
              onError={() => {
                setNftCoverUrl(nftDetail?.original?.image)
              }}
              alt=""
              className="w-full h-full max-w-full max-h-full object-contain"
            />
          )}
          <div
            className={styles.closeScreen}
            onClick={() => {
              setFullScreen(false)
              document.body.style.overflow = 'unset'
            }}
          >
            <Image src={CloseIcon} alt="" width={18} height={18} />
          </div>
        </div>
      )}

      {fullScreenMerch && (
        <div className={styles.fullScreenMerch}>
          <div>
            <div className="w-[370px] h-[370px] sm:w-[500px] sm:w-[500px] lg:w-[600px] lg:h-[600px]">
              <img src={selectedMerchImage} alt="" className="w-full h-full max-w-full max-h-full object-contain" />
            </div>
            <div className="mt-[30px] flex justify-center gap-[10px]">
              {merchImages.map((item, index) => (
                <div
                  className={item.selected ? styles.selectedMerch : styles.unselectedMerch}
                  key={index}
                  onClick={() => {
                    let images = []
                    merchImages.map(merch => images.push(merch.url))
                    let list = []
                    images.map((image, idx) => list.push({ url: image, selected: index === idx }))
                    setMerchImages(list)
                    setSelectedMerchImage(list[index].url)
                  }}
                >
                  <img src={item.url} className="max-w-full max-h-full object-conver" alt="" />
                </div>
              ))}
            </div>
          </div>
          <div
            className={styles.closeScreen}
            onClick={() => {
              setFullScreenMerch(false)
              document.body.style.overflow = 'unset'
            }}
          >
            <Image src={CloseIcon} alt="" width={18} height={18} />
          </div>
        </div>
      )}
    </div>
  )
}

// Wrapper component that forces remount when navigating to same page with different id
const BrowseNFT = (props: BrowseNFTProps) => {
  const router = useRouter()
  const id = router.query.id as string

  return <BrowseNFTContent key={id} {...props} />
}

export default BrowseNFT

const LayoutWrapper = ({ children }: { children }) => {
  const viewport = useViewport()
  const isMobile = viewport === 'xs' || viewport === 'sm' || viewport === 'md'

  return <SecondaryLayout style={{ overflow: isMobile ? 'hidden' : 'visible' }}>{children}</SecondaryLayout>
}

BrowseNFT.getLayout = function getLayout(page) {
  return <LayoutWrapper>{page}</LayoutWrapper>
}

BrowseNFT.getInitialProps = async function ({ res, query }): Promise<BrowseNFTProps> {
  // set cache headers
  if (res) {
    // cache for 30m, shared cache for 1h
    res.setHeader('cache-control', 'max-age=1800, s-maxage=3600')
  }

  try {
    const metaplex = Metaplex.make(new Connection(process.env.PROVIDER_URL))

    let nftDetail = (await getNftFromDAS(metaplex, query.id)) ?? (await getNftFromRpc(metaplex, query.id))
    let editionDetail: EditionDetail = {
      print: nftDetail.supply?.edition && { number: nftDetail.supply?.edition },
      supply: nftDetail.supply?.current,
      maxSupply: nftDetail.supply?.max,
      isOriginal: !nftDetail.supply?.edition,
      isSingleNft: nftDetail.supply?.max === 0,
      isOpenEdition: !nftDetail.supply?.edition && nftDetail.supply?.max == null,
      isLimitedEdition: !nftDetail.supply?.edition && nftDetail.supply?.max > 0,
    }

    let nftOwner = await getNFTOwner(metaplex, new PublicKey(nftDetail.mintAddress))
    return {
      nftDetail,
      nftOwner,
      editionDetail,
    }
  } catch (e: any) {
    console.error(e)
    return {
      error: `could not load nft: ${query.id}: ${e.message}`,
    }
  }
}
